import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Calendar, Printer, Users, Wrench, CalendarCheck, Clock } from "@phosphor-icons/react";

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const fmtMin = (mins) => `${Math.floor(mins / 60)}h ${mins % 60}m`;
const fmtHours = (mins) => (mins / 60).toFixed(1);

const TABS = [
  { key: "staff", label: "Per-Worker", icon: Users },
  { key: "hours", label: "Hours (Table)", icon: Clock },
  { key: "operations", label: "Per-Operation", icon: Wrench },
  { key: "attendance", label: "Attendance", icon: CalendarCheck },
];

export default function WorkforceReports() {
  const [tab, setTab] = useState("staff");
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(iso(new Date()));

  return (
    <div className="space-y-6" data-testid="workforce-reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Workforce Reports</h1>
          <p className="text-xs text-zinc-600 mt-1">Productivity, attendance and operator output.</p>
        </div>
        <div className="flex items-end gap-2 no-print flex-wrap">
          <div>
            <label className="brut-label inline-flex items-center gap-1"><Calendar size={10} weight="bold" /> From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="brut-input mt-1" data-testid="wf-from" />
          </div>
          <div>
            <label className="brut-label inline-flex items-center gap-1"><Calendar size={10} weight="bold" /> To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="brut-input mt-1" data-testid="wf-to" />
          </div>
          <button onClick={() => window.print()} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="wf-print">
            <Printer size={14} weight="bold" /> Print
          </button>
        </div>
      </div>

      <div className="brut-card p-1 inline-flex flex-wrap gap-1 no-print">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`brut-tag inline-flex items-center gap-1 ${tab === t.key ? "brut-tag-dark" : "brut-tag-white"}`}
            data-testid={`wf-tab-${t.key}`}
          >
            <t.icon size={12} weight="bold" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "staff" && <StaffSummary from={from} to={to} />}
      {tab === "hours" && <HoursTable from={from} to={to} />}
      {tab === "operations" && <OperationsProductivity from={from} to={to} />}
      {tab === "attendance" && <Attendance from={from} to={to} />}
    </div>
  );
}

// Employee × Day hours table with weekly total column.
function HoursTable({ from, to }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      const r = await api.get(`/reports/staff-summary?date_from=${from}&date_to=${to}`);
      setData(r.data);
    })();
  }, [from, to]);
  if (!data) return <div className="p-6 text-center font-display uppercase">Loading…</div>;

  // Build sorted date list within the range.
  const dates = (() => {
    const out = []; const [y1, m1, d1] = from.split("-").map(Number); const [y2, m2, d2] = to.split("-").map(Number);
    const start = new Date(y1, m1 - 1, d1); const end = new Date(y2, m2 - 1, d2);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) out.push(iso(d));
    return out;
  })();

  return (
    <div className="brut-card p-4 overflow-x-auto" data-testid="hours-table">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-display uppercase text-lg">Hours Worked — {from} → {to}</h3>
        <div className="text-xs font-mono-ui text-zinc-600">Green cells = present · Grey = no shift</div>
      </div>
      <table className="brut-table">
        <thead>
          <tr>
            <th className="text-left">Worker</th>
            {dates.map((d) => <th key={d} className="text-right font-mono-ui" style={{ fontSize: 10 }}>{d.slice(5)}</th>)}
            <th className="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && <tr><td colSpan={dates.length + 2} className="text-center py-8 text-zinc-500">No timesheets in this range.</td></tr>}
          {data.rows.map((row) => {
            const byDate = new Map((row.daily || []).map((d) => [d.date, d.worked_minutes || 0]));
            const total = Array.from(byDate.values()).reduce((a, b) => a + b, 0);
            return (
              <tr key={row.staff_id}>
                <td>
                  <div className="font-bold">{row.staff_name}</div>
                  <div className="text-xs text-zinc-600">{row.designation}</div>
                </td>
                {dates.map((d) => {
                  const m = byDate.get(d);
                  return (
                    <td key={d} className={`text-right font-mono-ui ${m ? "bg-emerald-50" : "bg-slate-50 text-zinc-400"}`}>
                      {m ? fmtHours(m) : "—"}
                    </td>
                  );
                })}
                <td className="text-right font-mono-ui font-bold bg-amber-50">{fmtHours(total)}h</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StaffSummary({ from, to }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      const r = await api.get(`/reports/staff-summary?date_from=${from}&date_to=${to}`);
      setData(r.data);
    })();
  }, [from, to]);
  if (!data) return <div className="font-display uppercase">Loading...</div>;

  return (
    <div className="brut-card p-4" data-testid="staff-summary">
      <h2 className="font-display uppercase text-xl mb-3">Per-Worker · {from} → {to}</h2>
      <table className="brut-table">
        <thead>
          <tr><th>Worker</th><th>Role</th><th>Days</th><th>Hours</th><th>Total Produced</th><th>Avg/day</th></tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-500">No timesheets in this range.</td></tr>}
          {data.rows.map((r) => (
            <tr key={r.staff_id} data-testid={`sr-${r.staff_name}`}>
              <td className="font-bold">{r.staff_name}</td>
              <td><span className="brut-tag brut-tag-yellow">{r.designation}</span></td>
              <td className="font-mono-ui">{r.days}</td>
              <td className="font-mono-ui">{fmtMin(r.worked_minutes)}</td>
              <td className="font-mono-ui font-bold">{r.total_produced}</td>
              <td className="font-mono-ui">{r.days > 0 ? Math.round(r.total_produced / r.days) : 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OperationsProductivity({ from, to }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      const r = await api.get(`/reports/operations-productivity?date_from=${from}&date_to=${to}`);
      setData(r.data);
    })();
  }, [from, to]);
  if (!data) return <div className="font-display uppercase">Loading...</div>;

  return (
    <div className="brut-card p-4" data-testid="ops-productivity">
      <h2 className="font-display uppercase text-xl mb-3">Per-Operation · {from} → {to}</h2>
      <table className="brut-table">
        <thead>
          <tr><th>Operation</th><th>Name</th><th>Sessions</th><th>Hours</th><th>Produced</th><th>Avg pcs/hr</th><th>Target/hr</th><th>vs Target</th></tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-zinc-500">No timesheet data for operations in this range.</td></tr>}
          {data.rows.map((r) => {
            const vs = r.target_per_hour ? Math.round((r.avg_per_hour / r.target_per_hour) * 100) : null;
            return (
              <tr key={r.operation_id} data-testid={`opp-${r.operation_code}`}>
                <td className="font-mono-ui font-bold">{r.operation_code}</td>
                <td>{r.operation_name}</td>
                <td className="font-mono-ui">{r.sessions}</td>
                <td className="font-mono-ui">{r.hours}</td>
                <td className="font-mono-ui font-bold">{r.produced}</td>
                <td className="font-mono-ui">{r.avg_per_hour}</td>
                <td className="font-mono-ui">{r.target_per_hour ?? "—"}</td>
                <td>
                  {vs != null && (
                    <span className={`brut-tag ${vs >= 100 ? "brut-tag-green" : vs >= 80 ? "brut-tag-yellow" : "brut-tag-red"}`}>
                      {vs}%
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Attendance({ from, to }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      const r = await api.get(`/reports/attendance?date_from=${from}&date_to=${to}`);
      setData(r.data);
    })();
  }, [from, to]);
  if (!data) return <div className="font-display uppercase">Loading...</div>;

  // Cap at ~14 days for visual sanity in a heatmap
  const dates = data.dates.slice(0, 21);

  return (
    <div className="brut-card p-4 overflow-x-auto" data-testid="attendance">
      <h2 className="font-display uppercase text-xl mb-3">Attendance Heatmap · {from} → {to}</h2>
      <table className="brut-table">
        <thead>
          <tr>
            <th className="min-w-[140px]">Worker</th>
            {dates.map((d) => <th key={d} className="text-center text-[10px] font-mono-ui">{d.slice(5)}</th>)}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && <tr><td colSpan={dates.length + 2} className="text-center py-6 text-zinc-500">No active staff.</td></tr>}
          {data.rows.map((r) => {
            const total = r.cells.reduce((a, c) => a + (c.produced || 0), 0);
            return (
              <tr key={r.staff_id} data-testid={`at-${r.staff_name}`}>
                <td>
                  <div className="font-bold">{r.staff_name}</div>
                  <div className="text-xs text-zinc-600">{r.designation}</div>
                </td>
                {r.cells.slice(0, 21).map((c) => (
                  <td key={c.date} className="text-center font-mono-ui text-xs" style={{ background: heatColor(c.produced, c.present) }} title={`${c.date}: ${c.present ? c.produced + " pcs" : "absent"}`}>
                    {c.present ? c.produced : "·"}
                  </td>
                ))}
                <td className="font-mono-ui font-bold">{total}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function heatColor(qty, present) {
  if (!present) return "#f3f4f6";
  if (qty === 0) return "#fef3c7";
  if (qty < 50) return "#dbeafe";
  if (qty < 150) return "#93c5fd";
  if (qty < 300) return "#3b82f6";
  return "#1d4ed8";
}
